import React, {useContext, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";

// Utils
import {filterReviewsForGate, toWeatherContext} from "./reviewConditions.js";

// Components
import Modal from "../../components/Modal.js";
import Review from "./Review.js";

// Styles
import {heroTheme, pad} from "../../style/components/variables.js";
import {Button, Heading, Loader, NotLoaded, Text} from "../../style/components/general.js";

dayjs.extend(utc);
dayjs.extend(timezone);

const ModalReview = ({
  visible,
  setVisible,
  goBack,
  checksheet,
  preview,
  previewRecord,
  confirm,
  date,
  reviews: reviewsOverride
}) => {
  const [loading, setLoading] = useState(
    Object.fromEntries(checksheet?.reviews?.map(({id}) => [id, true]))
  );
  const {currentUser} = useContext(AuthContext);
  const {settings} = useContext(SettingsContext);

  // Gate: today's precip + yesterday weather from checksheet payload.
  const weatherContext = useMemo(
    () => toWeatherContext(checksheet?.weather, checksheet?.precipitationChance),
    [checksheet]
  );

  const dateForConditional = useMemo(
    () => dayjs.utc().tz(settings?.timezone || "US/Pacific"),
    [settings]
  );

  const reviewsToShow = useMemo(() => {
    if (preview) return null;
    if (reviewsOverride) return reviewsOverride;
    return filterReviewsForGate(checksheet?.reviews, {dateForConditional, weatherContext});
  }, [preview, reviewsOverride, checksheet, dateForConditional, weatherContext]);

  const allLoaded = useMemo(() => {
    if (!reviewsToShow) return false;
    return reviewsToShow.every(({id}) => !loading[id]);
  }, [reviewsToShow, loading]);

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      hasBackButton={goBack !== undefined}
      goBack={() => goBack && goBack()}
      allowClose={preview !== undefined}>
      <Heading>
        <Icon icon={faTriangleExclamation} />
        &nbsp; Required Reviews
      </Heading>

      {!preview &&
        reviewsToShow?.map(({id, data, endAfter}) => (
          <Review
            key={id}
            checksheet={checksheet}
            review={{...data, end: endAfter, id}}
            setRecord={previewRecord}
            date={date}
            loading={loading[id]}
            setLoading={setLoading}
          />
        ))}

      {!preview && !allLoaded && (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      )}

      {!preview && allLoaded && (
        <>
          <Certification quiet>
            I {currentUser.firstName} {currentUser.lastName}, certify that I have reviewed the
            information provided for this checksheet.
          </Certification>
          <Button
            type="button"
            onClick={() => {
              if (confirm) confirm();
              else setVisible(false);
            }}>
            Agree & Continue
          </Button>
        </>
      )}

      {preview && (
        <Review
          checksheet={checksheet}
          review={preview}
          date={date}
          setLoading={() => {}}
          loading={false}
        />
      )}
    </Modal>
  );
};

ModalReview.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  goBack: PropTypes.func,
  checksheet: PropTypes.objectOf(PropTypes.any).isRequired,
  preview: PropTypes.objectOf(PropTypes.any),
  previewRecord: PropTypes.func,
  confirm: PropTypes.func,
  date: PropTypes.string,
  reviews: PropTypes.arrayOf(PropTypes.any)
};

// Style Overrides
const Icon = styled(FontAwesomeIcon)`
  fill: ${heroTheme.warning};
`;

const Certification = styled(Text)`
  margin: ${pad}px 0;
`;

export default ModalReview;
